import { useState, useEffect } from 'react';
import {
  XMarkIcon,
  PrinterIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { settingService } from '../../services/settingService';

interface DeliveryNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  sale: any;
}

interface CompanySettings {
  name: string;
  address?: string;
  phone?: string;
  email?: string;
  logo?: string;
}

// ponytail: preview-only document generated from the sale's own items, nothing
// new saved to the DB or audit trail — same pattern as the Pro-Forma Invoice preview.
export const DeliveryNoteModal = ({ isOpen, onClose, sale }: DeliveryNoteModalProps) => {
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    name: 'Aperture Camera Store',
    address: 'Nairobi, Kenya',
    phone: '+254 700 000 000',
    email: 'info@aperture.com',
  });

  useEffect(() => {
    if (isOpen) {
      settingService
        .getCompanySettings()
        .then((response) => {
          if (response.data?.data) {
            setCompanySettings((prev) => ({ ...prev, ...response.data.data }));
          }
        })
        .catch(() => {
          // Use defaults
        });
    }
  }, [isOpen]);

  if (!isOpen || !sale) return null;

  const customerName = sale.customers
    ? sale.customers.company_name || `${sale.customers.first_name || ''} ${sale.customers.last_name || ''}`.trim()
    : sale.customer_name || 'Walk-in Customer';

  const deliveryNoteNumber = `DN-${sale.sale_number.replace(/^SALE-/, '')}`;
  const totalQty = (sale.sale_items || []).reduce((sum: number, item: any) => sum + Number(item.quantity), 0);

  const getItemName = (item: any) =>
    item.is_bundle
      ? item.product_bundles?.bundle_name || 'Bundle'
      : item.products?.product_name || 'Unknown Item';

  const getItemSku = (item: any) =>
    item.is_bundle ? item.product_bundles?.bundle_sku : item.products?.sku;

  const getSerials = (item: any) =>
    (item.sale_item_serials || [])
      .map((s: any) => s.serial_numbers?.serial_number)
      .filter(Boolean);

  const handlePrint = () => window.print();

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 print:hidden">
              <h2 className="text-lg font-semibold text-gray-900">Delivery Note Preview</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Document */}
            <div className="p-8 overflow-auto max-h-[70vh] print:max-h-none print:overflow-visible">
              <div
                className="mx-auto bg-white print:shadow-none"
                style={{
                  width: '210mm',
                  minHeight: '297mm',
                  padding: '20mm',
                  boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                }}
              >
                {/* Company Header */}
                <div className="flex justify-between items-start mb-8">
                  <div>
                    {companySettings.logo && (
                      <img src={companySettings.logo} alt="Company Logo" className="h-16 object-contain mb-2" />
                    )}
                    <h1 className="text-2xl font-bold text-gray-900">{companySettings.name}</h1>
                    <div className="text-sm text-gray-600 mt-1 space-y-1">
                      {companySettings.address && (
                        <div className="flex items-center gap-1">
                          <MapPinIcon className="w-4 h-4" />
                          {companySettings.address}
                        </div>
                      )}
                      {companySettings.phone && (
                        <div className="flex items-center gap-1">
                          <PhoneIcon className="w-4 h-4" />
                          {companySettings.phone}
                        </div>
                      )}
                      {companySettings.email && (
                        <div className="flex items-center gap-1">
                          <EnvelopeIcon className="w-4 h-4" />
                          {companySettings.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <h2 className="text-3xl font-bold text-gray-400 uppercase tracking-wider">Delivery Note</h2>
                    <p className="text-lg font-semibold text-gray-900 mt-1">{deliveryNoteNumber}</p>
                  </div>
                </div>

                {/* Deliver To / Delivery Info */}
                <div className="flex justify-between mb-8">
                  <div className="w-1/2 pr-4">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Deliver To:</h3>
                    <div className="border border-gray-200 rounded-lg p-4">
                      <p className="font-semibold text-gray-900">{customerName}</p>
                      {sale.customers?.email && <p className="text-gray-600 mt-1">{sale.customers.email}</p>}
                      {sale.customers?.phone && <p className="text-gray-600 mt-1">{sale.customers.phone}</p>}
                    </div>
                  </div>
                  <div className="w-1/2 pl-4">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Delivery Details:</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Date:</span>
                        <span className="font-medium">{new Date().toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sale Reference:</span>
                        <span className="font-medium">{sale.sale_number}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Items:</span>
                        <span className="font-medium">{totalQty}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Items Table */}
                <table className="w-full mb-8">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#</th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="py-3 px-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Qty
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(sale.sale_items || []).map((item: any, index: number) => {
                      const serials = getSerials(item);
                      return (
                        <tr key={item.item_id ?? index}>
                          <td className="py-4 px-4 text-sm text-gray-900 align-top">{index + 1}</td>
                          <td className="py-4 px-4 text-sm text-gray-900 align-top">
                            <div>{getItemName(item)}</div>
                            {getItemSku(item) && (
                              <span className="text-gray-500 block text-xs">SKU: {getItemSku(item)}</span>
                            )}
                            {serials.length > 0 && (
                              <div className="mt-1 text-xs text-gray-600">
                                <span className="font-medium">Serial(s):</span> {serials.join(', ')}
                              </div>
                            )}
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-900 text-center align-top">{item.quantity}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Signatures */}
                <div className="flex justify-between mt-16 pt-8">
                  <div className="w-2/5">
                    <div className="border-t border-gray-400 pt-2">
                      <p className="text-sm text-gray-600">Delivered By (Name & Signature)</p>
                    </div>
                  </div>
                  <div className="w-2/5">
                    <div className="border-t border-gray-400 pt-2">
                      <p className="text-sm text-gray-600">Received By (Name & Signature)</p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-12 pt-8 border-t border-gray-200 text-center">
                  <p className="text-sm text-gray-500">
                    Please inspect goods on delivery and note any discrepancies above before signing.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 p-4 border-t border-gray-200 print:hidden">
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <PrinterIcon className="w-5 h-5" />
                Print / Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
      `}</style>
    </div>
  );
};
